import React, { useMemo } from 'react';

interface UserAvatarProps {
  name?: string;
  lastName?: string;
  profileImage?: string;
  size?: number;
  fontSize?: string;
  status?: 'active' | 'pending' | null;
  showRing?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  lastName,
  profileImage,
  size = 40,
  fontSize = '1rem',
  status = null,
  showRing = false,
}) => {
  const initials = useMemo(() => {
    if (!name && !lastName) return '?';
    const first = name?.charAt(0).toUpperCase() || '';
    const last = lastName?.charAt(0).toUpperCase() || '';
    return `${first}${last}`;
  }, [name, lastName]);

  const avatarColor = useMemo(() => {
    const fullName = `${name || ''}${lastName || ''}`.trim();
    if (!fullName) return '#007AFF';

    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colors = [
      '#007AFF', '#34C759', '#FF9500', '#AF52DE',
      '#FF2D55', '#5AC8FA', '#FFCC00', '#FF3B30',
      '#8E8E93', '#5856D6'
    ];
    return colors[Math.abs(hash) % colors.length];
  }, [name, lastName]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Editing Ring */}
      {showRing && (
        <div
          data-testid="ring-indicator"
          style={{
            position: 'absolute',
            inset: -6,
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, #007AFF, #34C759, #AF52DE, #007AFF)',
            opacity: 0.7,
            animation: 'spin 3s linear infinite',
            zIndex: 0,
          }}
        />
      )}

      {/* Status Indicator */}
      {status && (
        <div
          data-testid="status-indicator"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: status === 'active' ? 16 : 14,
            height: status === 'active' ? 16 : 14,
            borderRadius: '50%',
            background: status === 'active' ? '#34C759' : '#FF9500',
            border: '3px solid var(--card-bg)',
            zIndex: 2,
            boxShadow: status === 'active' ? '0 0 0 2px rgba(52, 199, 89, 0.4)' : 'none',
          }}
        />
      )}

      {/* Main Avatar — image or initials fallback */}
      {profileImage ? (
        <img
          src={profileImage}
          alt={`${name || ''} ${lastName || ''}`.trim() || 'User'}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            fontSize,
            fontWeight: 700,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '4px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease',
            zIndex: 1,
            display: 'block',
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            fontSize,
            fontWeight: 700,
            backgroundColor: avatarColor,
            border: '4px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            position: 'relative',
          }}
        >
          {initials}
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
